import React, { useEffect, useRef, useState } from 'react'
import { useApiFormProviderOrganization } from '../../api-new/state/auth'
import { Pill } from './pill'
import { serverMessage } from './format'
import { ErrorReference } from '../error/error-reference'
import { useErrorLine } from '../error/use-error-line'

// How a sign-in through the connection gets into one linked organization
// (provider_organizations.join_policy). "choose" and "code" only reach
// people the connection's other organizations don't place anywhere.
export const JOIN_POLICIES = [
  { value: 'everyone', label: 'Everyone who signs in', hint: 'Every sign-in through this connection joins.' },
  { value: 'rule', label: 'Only people matching the rule', hint: 'People join when their claim matches the rule. Nobody else is added.' },
  { value: 'choose', label: 'People can choose to join', hint: 'Anyone your identity provider lets sign in can add themselves to this organization.' },
  { value: 'code', label: 'People with a join code', hint: 'People join by entering a code you give them. The code sets their role.' }
]

const byValue = Object.fromEntries(JOIN_POLICIES.map(p => [p.value, p]))

// Links saved before join policies existed come back without one: a rule
// means "rule", no rule "everyone" (what the server derives too).
export const linkJoinPolicy = link =>
  (link && link.joinPolicy) || (link && link.matchData && link.matchData.claim ? 'rule' : 'everyone')

export const joinPolicyLabel = policy => (byValue[policy] || byValue.everyone).label

const TONE = { everyone: 'warn', rule: 'ok', choose: 'info', code: 'info' }

export const JoinPolicyPill = ({ policy }) => <Pill tone={TONE[policy]}>{joinPolicyLabel(policy)}</Pill>

// Inline editor for one link's join policy: the four choices as a
// segmented group of cards. Saves on its own (PUT
// /provider-organization/:providerID/:organizationID/join-policy), never
// with the page's Save. "Only people matching the rule" needs a rule.
export const JoinPolicyEditor = ({ link, providerID, onSaved, onCancel }) => {
  const api = useApiFormProviderOrganization()
  const [policy, setPolicy] = useState(linkJoinPolicy(link))
  const [saving, setSaving] = useState(false)
  const [error, failure, setError] = useErrorLine()
  const first = useRef(null)
  const hasRule = Boolean(link.matchData && link.matchData.claim)
  const name = `sw-policy-${link.organizationID}`

  useEffect(() => { if (first.current) first.current.focus() }, [])

  const handleKeyDown = e => {
    if (e.key === 'Escape') { e.stopPropagation(); onCancel() }
    else if (e.key === 'Enter' && e.target.tagName === 'INPUT') e.preventDefault()
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      await api.put(`${providerID}/${link.organizationID}/join-policy`, { joinPolicy: policy })
      onSaved(policy)
    } catch (exp) {
      setError(serverMessage(exp, 'Could not save who joins.'), exp)
      setSaving(false)
    }
  }

  const unchanged = policy === linkJoinPolicy(link)

  return (
    <div className="sw-inline-panel" role="region" aria-label={`Who joins ${link.organizationName}`} onKeyDown={handleKeyDown}>
      <fieldset className="m-0">
        <legend className="sw-label mb-2">Who joins {link.organizationName}</legend>
        <div className="sw-segmented" role="radiogroup" aria-label={`Who joins ${link.organizationName}`}>
          {JOIN_POLICIES.map(p => {
            const disabled = p.value === 'rule' && !hasRule
            return (
              <label key={p.value} className={`sw-choice${policy === p.value ? ' sw-checked' : ''}${disabled ? ' sw-disabled' : ''}`}>
                <input type="radio" className="form-check-input mt-1" name={name} value={p.value} checked={policy === p.value} disabled={disabled}
                  onChange={() => setPolicy(p.value)} ref={policy === p.value ? first : null} />
                <span>
                  <strong className="d-block">{p.label}</strong>
                  <span className="sw-hint">{disabled ? 'Add a rule for this organization first.' : p.hint}</span>
                </span>
              </label>
            )
          })}
        </div>
      </fieldset>
      {(policy === 'choose' || policy === 'code') &&
        <p className="sw-note m-0">
          Only offered to people this connection doesn't already place in an organization.
          {hasRule && ' The rule still applies: only people matching it are offered this organization.'}
        </p>}
      {error && <p className="text-danger small m-0" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} className="mt-0" />}
      <div className="sw-inline-actions">
        <button type="button" className="btn sw-btn" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn sw-btn-primary" disabled={unchanged || saving} onClick={handleSave}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </div>
  )
}
